import { Navigate, useParams, useNavigate } from '@tanstack/react-router';
import { Button, Card, Divider, IconButton, Typography, useToast } from '@/primer/components';
import { PageColumn, PageLoadingState } from '@/components/layout';
import { useVerifiedAddresses, useCampaign, useClaimCampaign } from '@/api';
import { useMixpanel } from '@/contexts';
import { COUNTRY_OPTIONS } from '@/schemas';
import { findReservedUserProduct, trackFunnelEvent, CREATOR_FUNNEL_EVENTS } from '@/utils';
import { ReservationTimerBanner } from '@/components/ui';
import { getApiErrorMessage } from '@/utils/apiError';
import { captureError } from '@/config/sentry';

/**
 * Address review screen in the campaign participation flow.
 *
 * Shows the creator's most recent saved address with an option to edit.
 * If no address is saved, redirects to the edit screen automatically.
 * Continuing claims the product against this address and, on success, moves to
 * the confirmation screen.
 */
export function CampaignAddress() {
  const { id } = useParams({ from: '/_authenticated/campaign/$id/address' });
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { data: campaign, isLoading: campaignLoading, error: campaignError } = useCampaign(id);
  const {
    data: addressesResponse,
    isLoading: addressLoading,
    error: addressError,
  } = useVerifiedAddresses();
  const claimMutation = useClaimCampaign();
  const { mixpanel } = useMixpanel();

  const isLoading = campaignLoading || addressLoading;

  const reservedUserProduct = findReservedUserProduct(campaign?.userProducts);

  const latestAddress = addressesResponse?.data?.length
    ? addressesResponse.data[addressesResponse.data.length - 1]
    : null;

  if (campaignError || addressError) {
    return (
      <PageColumn className="py-6 flex flex-col gap-4">
        <Typography variant="header" size="h1" as="h1">
          Unable to load shipping details
        </Typography>
        <Button onClick={() => navigate({ to: '/dashboard' })}>Back to Home</Button>
      </PageColumn>
    );
  }

  if (isLoading) {
    return <PageLoadingState />;
  }

  if (!latestAddress) {
    return <Navigate to="/campaign/$id/address-edit" params={{ id }} replace />;
  }

  // TODO - PD-327179: guard Claim by whether the creator currently has a
  // reserved product for this campaign (and handle reservation expiry).
  const handleClaim = async () => {
    if (claimMutation.isPending) return;
    try {
      await claimMutation.mutateAsync({
        campaignId: id,
        request: { userVerifiedAddressId: latestAddress.id },
      });
    } catch (err) {
      if (err instanceof Error) captureError(err);
      showToast({
        variant: 'error',
        message: getApiErrorMessage(err, {
          messageMap: { 'Already claimed': 'This product has already been claimed.' },
          fallback: "We couldn't finalize your participation. Please try again.",
        }),
      });
      return;
    }

    const productId = reservedUserProduct?.casProductId;

    trackFunnelEvent(
      mixpanel,
      CREATOR_FUNNEL_EVENTS.PRODUCT_CLAIMED,
      {
        campaign_id: String(id),
        campaign_name: campaign?.name ?? '',
        product_id: String(productId),
      },
      [productId, campaign?.name]
    );

    navigate({ to: '/campaign/$id/accepted', params: { id } });
  };

  return (
    <PageColumn className="py-6 flex flex-col gap-6">
      {reservedUserProduct?.reservedDate && (
        <ReservationTimerBanner campaignId={id} reservedDate={reservedUserProduct.reservedDate} />
      )}
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <IconButton
          icon="chevron-left"
          onClick={() => navigate({ to: '/campaign/$id/tasks', params: { id }, replace: true })}
          aria-label="Go back"
        />
        <Typography variant="header" size="h1" as="h1">
          {campaign?.name ?? 'Your collab'}
        </Typography>
      </div>

      <Divider />

      {/* ── Body ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4">
        <Typography variant="body" size="medium" color="header" decoration={{ bold: true }}>
          Double-check your shipping details <span aria-hidden="true">👀</span>
        </Typography>

        <Card>
          <div className="flex flex-col gap-1 p-4">
            <Typography variant="body" size="medium" color="header" decoration={{ bold: true }}>
              {latestAddress.firstName} {latestAddress.lastName}
            </Typography>
            <Typography variant="body" size="medium">
              {latestAddress.street}
              {latestAddress.apartmentNumber ? `, ${latestAddress.apartmentNumber}` : ''}
            </Typography>
            <Typography variant="body" size="medium">
              {latestAddress.city}
            </Typography>
            <Typography variant="body" size="medium">
              {latestAddress.subdivision}, {latestAddress.postalCode}
            </Typography>
            <Typography variant="body" size="medium">
              {COUNTRY_OPTIONS.find((country) => country.value === latestAddress.countryCode)
                ?.label ?? latestAddress.countryCode}
            </Typography>
            {latestAddress.phoneNumber && (
              <Typography variant="body" size="medium">
                {latestAddress.phoneNumber}
              </Typography>
            )}
            <div className="mt-3">
              <Button
                variant="secondary"
                size="medium"
                onClick={() =>
                  navigate({
                    to: '/campaign/$id/address-edit',
                    params: { id },
                    search: { addressId: latestAddress.id },
                  })
                }
              >
                Edit
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* ── Claim button ──────────────────────────────────────────────── */}
      <Button
        variant="primary"
        fullWidth
        size="large"
        onClick={handleClaim}
        isLoading={claimMutation.isPending}
        disabled={claimMutation.isPending}
      >
        Claim
      </Button>
    </PageColumn>
  );
}

export default CampaignAddress;
